/**
 * session.component.tsx
 * Secured Communication
 *
 * Created by: Dylan Knight
 * Copyright (c) 2021 Secured Communications. All rights reserved.
 *
**/

import bluebird from "bluebird";
import * as React from "react";
//import { RouteComponentProps } from "react-router-dom";
import OT from "@opentok/client";


import Check from "@mui/icons-material/Check";
import LogoutIcon from "@mui/icons-material/Logout";
import MenuIcon from "@mui/icons-material/Menu";
import MicIcon from "@mui/icons-material/Mic";
import MicOffIcon from "@mui/icons-material/MicOff";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ScreenShareIcon from "@mui/icons-material/ScreenShare";
import StopScreenShareIcon from "@mui/icons-material/StopScreenShare";
import VideocamIcon from "@mui/icons-material/Videocam";
import VideocamOffIcon from "@mui/icons-material/VideocamOff";
import { ClickAwayListener, Divider, Fab, IconButton, ListItemIcon, ListItemText, ListSubheader, MenuItem, 
  MenuList, Paper, Popper, ToggleButton, ToggleButtonGroup, Tooltip } from "@mui/material";

import { GroupNavContext } from "../chat/groupNav.context";
import { ErrorSnackBar } from "../components/errorSnackBar.component";
import { SessionStreamsComponent } from "./sessionStreams.component";

import { SubscriberStream, AspectRatio } from "../models/stream";

import { authServiceFactory } from "../services/auth.service";
import { conferenceServiceFactory } from "../services/conferencing.service";


interface SessionComponentProps {
  session: OT.Session,
  token: string,
  sendVideo: boolean,
  sendAudio: boolean,
  isFullscreen: boolean,
  videoSrcID?: string,
  audioSrcID?: string,
  onToggleVideo?: () => void,
  onToggleAudio?: () => void,
  onToggleFullscreen?: () => void,
  onLeaveRoom: () => void,
  onPublisherDestroyed: () => boolean,
  setVideoSourceID?: (srcID: string) => void,
  setAudioSourceID?: (srcID: string) => void
  //onError: (error: Error) => void
}

interface State {
  errMsg: string,
  connecting: boolean,
  screenPublisher: OT.Publisher | null,
  streams: Array<SubscriberStream>,
  aspectRatio: AspectRatio,
  canScreenShare: boolean,
  openMenu: boolean,
  anchorEl: HTMLElement | null,
  videoDevices: Array<OT.Device>,
  audioDevices: Array<OT.Device>
}

export class SessionComponent extends React.Component<SessionComponentProps, State> {
  static contextType = GroupNavContext;

  private container: React.RefObject<HTMLDivElement | null>;

  /**
   * This the source of truth for the streams. Because the streams arrive
   * from outside react, the state might be out of date when we try to update
   * state.streams. This array, however, can be updated directly, and the new
   * state can be derived from it.
   */
  private streams: Array<SubscriberStream>;

  /**
   * Since state might not update immediately, we need to keep track of the 
   * publisher as soon as it's created in order to destroy it
   */
  private publisher: OT.Publisher | null;

  constructor(props: SessionComponentProps) {
    super(props);

    this.container = React.createRef<HTMLDivElement>();
    this.onSessionDisconnected = this.onSessionDisconnected.bind(this);
    this.onSessionReconnected = this.onSessionReconnected.bind(this);
    this.onSessionReconnecting = this.onSessionReconnecting.bind(this);
    this.onStreamCreated = this.onStreamCreated.bind(this);
    this.onStreamDestroyed = this.onStreamDestroyed.bind(this);
    this.createPublisher = this.createPublisher.bind(this);
    this.publisherDestroyed = this.publisherDestroyed.bind(this);
    this.leave = this.leave.bind(this);
    this.toggleScreenShare = this.toggleScreenShare.bind(this);
    this.setupScreenSharing = this.setupScreenSharing.bind(this);
    this.doOpenMenu = this.doOpenMenu.bind(this);
    this.changeAspectRatio = this.changeAspectRatio.bind(this);
    this.screenPublisherDestroyed = this.screenPublisherDestroyed.bind(this);

    const fakeStreams: Array<SubscriberStream> = [];
    //for (let i = 0; i < 4; i++) { fakeStreams.push(new SubscriberStream(i)); }
    this.publisher = null;

    this.streams = fakeStreams;
    this.state = {
      errMsg: "",
      connecting: false,
      streams: this.streams,
      aspectRatio: AspectRatio.widescreen,
      canScreenShare: false,
      screenPublisher: null,
      openMenu: false,
      anchorEl: null,
      videoDevices: [],
      audioDevices: []
    };
  }

  async componentDidMount() {
    this.props.session.on("sessionReconnecting", this.onSessionReconnecting);
    this.props.session.on("sessionReconnected", this.onSessionReconnected);
    this.props.session.on("sessionDisconnected", this.onSessionDisconnected);
    this.props.session.on("streamCreated", this.onStreamCreated);
    this.props.session.on("streamDestroyed", this.onStreamDestroyed);

    try {
      OT.checkScreenSharingCapability(response => {
        this.setState({canScreenShare: response.supported});
      });

      this.setState({connecting: true});

      const publisher = await this.createPublisher();
      this.publisher = publisher;
      const [videoDevices, audioDevices] = await conferenceServiceFactory().getMediaDevices();

      await new bluebird<void>((resolve, reject) => {
        this.props.session.connect(this.props.token, error => {
          if (error) { return reject(error); }
          resolve();
        });
      });

      await this.connectPublisher(publisher);

      this.setState({
        audioDevices: audioDevices,
        videoDevices: videoDevices
      });

    } catch (err) {
      console.error("Error setting up session:", err);
      this.setState({errMsg: (err as Error).message});
      const anyerr = err as any;
      if (anyerr.originalName && anyerr.originalName === "NotAllowedError") {
        this.setState({errMsg: `Unable to publish your A/V streams.
          Please, ensure you've allowed the site access to your camera and microphone!`});
      } else {
        this.setState({errMsg: (err as Error).message});
      }
    } finally {
      this.setState({connecting: false});
    }
  }

  componentWillUnmount() {
    this.props.session.off("streamCreated", this.onStreamCreated);
    this.props.session.off("streamDestroyed", this.onStreamDestroyed);
    this.props.session.off("sessionReconnecting", this.onSessionReconnecting);
    this.props.session.off("sessionReconnected", this.onSessionReconnected);
    this.props.session.off("sessionDisconnected", this.onSessionDisconnected);

    if (this.publisher) {
      this.publisher.publishAudio(false);
      this.publisher.publishVideo(false);
      this.publisher.destroy();
    }
    if (this.state.screenPublisher) {
      this.state.screenPublisher.publishAudio(false);
      this.state.screenPublisher.publishVideo(false);
      this.state.screenPublisher.off("streamDestroyed", this.screenPublisherDestroyed);
      this.state.screenPublisher.destroy();
    }
  }

  componentDidUpdate(prevProps: SessionComponentProps) {
    if (this.props.sendAudio !== prevProps.sendAudio) {
      this.publisher?.publishAudio(this.props.sendAudio);
    }
    if (this.props.sendVideo !== prevProps.sendVideo) {
      this.publisher?.publishVideo(this.props.sendVideo);
    }
    if (this.props.videoSrcID && this.props.videoSrcID !== prevProps.videoSrcID) {
      this.publisher?.setVideoSource(this.props.videoSrcID);
    }
    if (this.props.audioSrcID && this.props.audioSrcID !== prevProps.audioSrcID) {
      this.publisher?.setAudioSource(this.props.audioSrcID);
    }
  }

  onSessionReconnecting(event: OT.Event<"sessionReconnecting", OT.Session>) {
    console.log("Session attempting reconnect:", event);
  }

  onSessionReconnected(event: OT.Event<"sessionReconnected", OT.Session>) {
    console.log("Session reconnected:", event);
  }

  onSessionDisconnected(event: OT.Event<"sessionDisconnected", OT.Session>) {
    console.log("Session disconnected:", event);
    if ((event as any).reason === "networkDisconnected") {
      this.setState({errMsg:
        "You lost your internet connection. Please check your connection and try connecting again."
      });
    }
  }

  onStreamCreated(event: OT.Event<"streamCreated", OT.Session> & {stream: OT.Stream}) {
    const index = this.streams.findIndex(sub => sub.streamId === event.stream.streamId);
    if (index < 0) {
      this.streams.push(new SubscriberStream(event.stream));
      this.setState({streams: Array.from(this.streams)});
    }
  }

  onStreamDestroyed (event: OT.Event<"streamDestroyed", OT.Session> & {stream: OT.Stream, reason: string}) {
    const index = this.streams.findIndex(sub => {
      return sub.streamId === event.stream.streamId;
    });

    if (index >= 0) {
      this.streams.splice(index, 1);
      this.setState({streams: Array.from(this.streams)});
    } else {
      console.warn("no stream found here for id:", event.stream.streamId, this.streams);
    }
  }

  async createPublisher(): Promise<OT.Publisher> {
    const membership = authServiceFactory().currentAccountMembership;
    const pubName = membership ? membership.firstname + " " + membership.lastname : "";
    const publisher = await conferenceServiceFactory().createPublisher("publisher", pubName,
          this.props.sendAudio, this.props.sendVideo);
    if (this.props.videoSrcID) {
      publisher.setVideoSource(this.props.videoSrcID);
    }
    if (this.props.audioSrcID) {
      publisher.setAudioSource(this.props.audioSrcID);
    }
    publisher.on("destroyed", this.publisherDestroyed);
    return publisher;
  }

  connectPublisher(publisher: OT.Publisher): Promise<void> {
    return new bluebird<void>((resolve, reject) => {
      this.props.session.publish(publisher, error => {
        if (error) {
          publisher.destroy();
          return reject(error);
        }
        resolve();
      });
    });
  }

  // disconnectPublisher(publisher: OT.Publisher) {
  //   publisher.publishAudio(false);
  //   publisher.publishVideo(false);
  //   publisher.destroy();
  // }

  leave() {
    if (this.publisher) {
      this.publisher.publishVideo(false);
      this.publisher.publishAudio(false);
    }
    this.props.onLeaveRoom();
  }

  async publisherDestroyed (/*event: OT.Event<"destroyed", OT.Publisher>*/) {
    if (this.props.onPublisherDestroyed()) {
      this.publisher?.off("streamDestroyed", this.publisherDestroyed);
      this.publisher = null;
      const publisher = await this.createPublisher();
      await this.connectPublisher(publisher);
      this.publisher = publisher;
    }
  }

  screenPublisherDestroyed () {
    this.setState({screenPublisher: null});
  }

  toggleScreenShare() {
    if (this.state.screenPublisher) {
      this.state.screenPublisher.destroy();
      this.setState({screenPublisher: null});
    } else {
      this.setupScreenSharing();
    }
  }

  async setupScreenSharing() {
    try {
      const membership = authServiceFactory().currentAccountMembership;
      const pubName = membership ?
        membership.firstname + "'s Screen" : "Screen Share";
      const publisher = await conferenceServiceFactory().createScreenShare(
          "screen-share", pubName, this.props.sendAudio);

      this.setState({screenPublisher: publisher});

      await new bluebird<OT.Publisher>((resolve, reject) => {
        this.props.session.publish(publisher, error => {
          if (error) { return reject(error); }
          resolve();
        });
      });

      publisher.on("streamDestroyed", this.screenPublisherDestroyed);

    } catch (err) {
      const anyErr = err as any;
      if (anyErr.originalName && anyErr.originalName === "NotAllowedError") {
        this.setState({
          errMsg: "Please, enable access to screenshare.",
          screenPublisher: null
        });
      }
      else if (!anyErr.originalName || anyErr.originalName != "NotAllowedError") {
        console.error("Error trying to share screen:", err);
        this.setState({
          errMsg: (err as Error).message,
          screenPublisher: null
        });
      }
    }
  }

  doOpenMenu (event: React.MouseEvent<HTMLButtonElement>) {
    this.setState({
      anchorEl: event.currentTarget,
      openMenu: !this.state.openMenu
    });
  }

  changeAspectRatio(_event: React.MouseEvent<HTMLElement>, ratio: AspectRatio) {
    this.setState({aspectRatio: ratio});
  }

  render(): React.ReactElement<any, any> {
    const context = this.context as GroupNavContext;
    return <div className="room-session-container" ref={this.container}>
      <ErrorSnackBar errMsg={this.state.errMsg} didClose={() => this.setState({errMsg: ""})} />
      <SessionStreamsComponent
        session={this.props.session}
        connecting={this.state.connecting}
        screenPublisher={this.state.screenPublisher}
        isPublishing={this.publisher !== null}
        streams={this.state.streams}
        availableWidth={this.container.current?.clientWidth}
        availableHeight={this.container.current?.clientHeight}
        aspectRatio={this.state.aspectRatio} />
      <div className="room-controls-backdrop">
        <div className="room-controls">
          <IconButton size="small"
              disableRipple disableTouchRipple onClick={context.toggleGroupList}>
            <MenuIcon />
          </IconButton>
          <IconButton size="medium" aria-label={this.props.sendVideo ? "Disable Video" : "Enable Video"}
              disableRipple disableTouchRipple onClick={this.props.onToggleVideo}>
            <Tooltip title={this.props.sendVideo ? "Disable Video" : "Enable Video"} arrow>
              {this.props.sendVideo ? <VideocamIcon /> : <VideocamOffIcon />}
            </Tooltip>
          </IconButton>
          <IconButton size="medium" aria-label={this.props.sendAudio ? "Disable Audio" : "Enable Audio"}
              disableRipple disableTouchRipple onClick={this.props.onToggleAudio}>
            <Tooltip title={this.props.sendAudio ? "Disable Audio" : "Enable Audio"} arrow>
              {this.props.sendAudio ? <MicIcon /> : <MicOffIcon />}
            </Tooltip>
          </IconButton>
          {this.state.canScreenShare && <IconButton size="medium" onClick={this.toggleScreenShare}
              aria-label={this.state.screenPublisher ? "Stop Sharing" : "Share Screen"}>
            <Tooltip title={this.state.screenPublisher ? "Stop Sharing" : "Share Screen"} arrow>
              {this.state.screenPublisher ? <StopScreenShareIcon/> : <ScreenShareIcon />}
            </Tooltip>
          </IconButton>}
          <IconButton size="medium"
              disableRipple disableTouchRipple onClick={this.props.onToggleFullscreen}>
            <Tooltip title={this.props.isFullscreen ? "Collapse" : "Expand"} arrow>
              <i className={"fas " + (this.props.isFullscreen ? "fa-compress-alt" : "fa-expand-alt")}></i>
            </Tooltip>
          </IconButton>
          <IconButton disableRipple disableTouchRipple
              aria-label="huddle-more-menu" aria-haspopup="true"
              size="small" onClick={this.doOpenMenu}>
            <MoreVertIcon fontSize="small" />
          </IconButton>
          <Fab className="leave-room-btn" size="small" color="secondary" aria-label="Leave Room"
              disableRipple disableTouchRipple onClick={this.leave}>
            <LogoutIcon />
          </Fab>
          <Popper open={this.state.openMenu} anchorEl={this.state.anchorEl} disablePortal placement="top">
            <Paper>
            <ClickAwayListener onClickAway={() => this.setState({openMenu: false})}>
              <MenuList dense={true}>
                {this.state.videoDevices.length > 0 && <div>
                  <ListSubheader>Video Inputs</ListSubheader>
                  {this.state.videoDevices.map((d, i) =>
                    <MenuItem key={d.deviceId}
                        onClick={() => this.props.setVideoSourceID && this.props.setVideoSourceID(d.deviceId)}>
                      <ListItemIcon>
                        {i === 0 && !this.props.videoSrcID || this.props.videoSrcID === d.deviceId ? <Check />: <></>}
                      </ListItemIcon>
                      <ListItemText>{d.label}</ListItemText>
                    </MenuItem>)}
                  <Divider />
                </div>}
                {this.state.audioDevices.length > 0 && <div>
                  <ListSubheader>Audio Inputs</ListSubheader>
                  {this.state.audioDevices.map((d, i) =>
                    <MenuItem key={d.deviceId}
                        onClick={() => this.props.setAudioSourceID && this.props.setAudioSourceID(d.deviceId)}>
                      <ListItemIcon>
                        {i === 0 && !this.props.audioSrcID || this.props.audioSrcID === d.deviceId ? <Check /> : <></>}
                      </ListItemIcon>
                      <ListItemText>{d.label}</ListItemText>
                    </MenuItem>)}
                  <Divider />
                </div>}
                <ListSubheader>Aspect Ratio</ListSubheader>
                <MenuItem className="aspect-chooser-container" disableRipple disableTouchRipple>
                  <ToggleButtonGroup exclusive size="small" value={this.state.aspectRatio}
                      onChange={this.changeAspectRatio}>
                    <ToggleButton value={AspectRatio.square} disableRipple disableTouchRipple>1/1</ToggleButton>
                    <ToggleButton value={AspectRatio.tv} disableRipple disableTouchRipple>4/3</ToggleButton>
                    <ToggleButton value={AspectRatio.widescreen} disableRipple disableTouchRipple>16/9</ToggleButton>
                    <ToggleButton value={AspectRatio.anamorphic} disableRipple disableTouchRipple>2.39/1</ToggleButton>
                    <ToggleButton value={AspectRatio.phone} disableRipple disableTouchRipple>1/2</ToggleButton>
                  </ToggleButtonGroup>
                </MenuItem>
              </MenuList>
            </ClickAwayListener>
            </Paper>
          </Popper>
        </div>
      </div>
    </div>;
  }
}
